/* eslint-disable @typescript-eslint/no-unused-vars */
import { EditScheduleForm, Schedule } from '@dispatch/shared/types';
import { v4 as uuidv4 } from 'uuid';
import {
  createDatesOfWeek,
  createDateWithTimeZoneOffset,
  createDayNumberArray,
  getDateFromAddedDays,
} from './DayHelper';

export const ResourceScheduleHelper = (
  values: EditScheduleForm,
  resourceId: string,
  schedules: Schedule[],
  tenantId: string
): {
  hasDuplicate: boolean;
  schedules: Schedule[];
} => {
  const startDate = createDateWithTimeZoneOffset(
    values.startDateTime,
    values.timeZoneId
  );
  const endDate = createDateWithTimeZoneOffset(
    values.endDateTime,
    values.timeZoneId
  );

  if (values.repeat) {
    const repeatUntil = getDateFromAddedDays(startDate, +values.repeatFor);

    const datesToAdd: Schedule[] = [];
    const days = createDatesOfWeek(startDate, endDate);
    const dayNumbers = createDayNumberArray(values.days);

    dayNumbers.forEach((value) => {
      getRepeatedDates(
        datesToAdd,
        repeatUntil,
        resourceId,
        values.timeZoneId,
        schedules,
        tenantId,
        days[value]?.startDate,
        days[value]?.endDate
      );
    });

    const duplicateSchedules = getDuplicateSchedules_Repeat(
      schedules,
      datesToAdd
    );

    if (duplicateSchedules.length > 0) {
      return {
        hasDuplicate: true,
        schedules: duplicateSchedules,
      };
    }

    return {
      hasDuplicate: false,
      schedules: datesToAdd,
    };
  }

  const result = getDuplicateSchedules(
    schedules,
    startDate,
    endDate,
    values.timeZoneId,
    values.externalId
  );

  if (result.length > 0) {
    return {
      hasDuplicate: true,
      schedules: result,
    };
  }

  return {
    hasDuplicate: false,
    schedules: [
      createSchedule(
        startDate,
        endDate,
        resourceId,
        values.timeZoneId,
        tenantId,
        values.externalId,
        values?.dailyClaimOwnerTasksCount || 0
      ),
    ],
  };
};

const createSchedule = (
  startDate: Date,
  endDate: Date,
  resourceId: string,
  timeZoneId: string,
  tenantId: string,
  externalId?: string,
  dailyClaimOwnerTasksCount?: number
): Schedule => {
  // sanitize dates to not have seconds or ms
  startDate.setSeconds(0, 0);
  endDate.setSeconds(0, 0);

  return {
    resourceId: resourceId,
    externalId: externalId || uuidv4(),
    tenantId: tenantId, // hard coded
    workingDayStartTime: new Date(startDate)
      .toISOString()
      .replace(/.\d+Z$/g, 'Z'),
    workingDayEndTime: new Date(endDate).toISOString().replace(/.\d+Z$/g, 'Z'),
    timeZoneId: timeZoneId,
    dailyClaimOwnerTasksCount,
    isVisible: true,
  };
};

const getRepeatedDates = (
  repeatedDates: Schedule[],
  repeatUntil: Date,
  resourceId: string,
  timeZoneId: string,
  schedules: Schedule[],
  tenantId: string,
  startDate?: Date | null,
  endDate?: Date | null
): boolean => {
  if (!startDate || !endDate) {
    return false;
  }

  if (startDate <= repeatUntil) {
    repeatedDates.push(
      createSchedule(startDate, endDate, resourceId, timeZoneId, tenantId)
    );
    return getRepeatedDates(
      repeatedDates,
      repeatUntil,
      resourceId,
      timeZoneId,
      schedules,
      tenantId,
      getDateFromAddedDays(startDate, 7),
      getDateFromAddedDays(endDate, 7)
    );
  }

  return true;
};

const getDuplicateSchedules = (
  schedules: Schedule[],
  startDate: Date,
  endDate: Date,
  timeZone: string,
  externalId?: string
) => {
  const result = schedules.filter((schedule) => {
    if (externalId === schedule.externalId) {
      return false;
    }

    const rDateUTCStartDatetime = createDateWithTimeZoneOffset(
      startDate,
      timeZone
    ); // nothing should be null as this has passed the yup validation
    const rDateUTCEndDatetime = createDateWithTimeZoneOffset(endDate, timeZone); // nothing should be null as this has passed the yup validation

    return checkHasOverlap(
      schedule,
      rDateUTCStartDatetime,
      rDateUTCEndDatetime
    );
  });

  return result;
};

const getDuplicateSchedules_Repeat = (
  schedules: Schedule[],
  repeatedDates: Schedule[]
) => {
  const result = schedules.filter((schedule) => {
    return repeatedDates.find((rDate) => {
      if (rDate.externalId === schedule.externalId) {
        return false;
      }

      const rDateUTCStartDatetime = createDateWithTimeZoneOffset(
        new Date(rDate.workingDayStartTime),
        rDate.timeZoneId
      );

      const rDateUTCEndDatetime = createDateWithTimeZoneOffset(
        new Date(rDate.workingDayEndTime),
        rDate.timeZoneId
      );
      return checkHasOverlap(
        schedule,
        rDateUTCStartDatetime,
        rDateUTCEndDatetime
      );
    });
  });

  return result;
};

const checkHasOverlap = (
  schedule: Schedule,
  startDateTime: Date,
  endDateTime: Date
): boolean => {
  const scheduleStartDateTime = createDateWithTimeZoneOffset(
    new Date(schedule.workingDayStartTime),
    schedule.timeZoneId
  );
  const scheduleEndDateTime = createDateWithTimeZoneOffset(
    new Date(schedule.workingDayEndTime),
    schedule.timeZoneId
  );

  return (
    (startDateTime >= scheduleStartDateTime &&
      startDateTime <= scheduleEndDateTime) ||
    (endDateTime >= scheduleStartDateTime && endDateTime <= scheduleEndDateTime)
  );
};
